import React, { useEffect, useRef, useState } from 'react'
import { Box, Typography, Card, Stack, IconButton, Tooltip, Textarea } from '@mui/joy'
import { VoiceTranscript, VoiceTranscriptRole } from '@numeo/types'
import { convertVoiceAgentToolCallToMessage } from 'utils/convert-agent-tool-call-to-message'
import { CheckCircle, HelpOutline, Close, Check } from '@mui/icons-material'
import axios from 'axios'

interface AITreanscriptionPanelActiveCall {
    callId: string
    from: string
    to: string
    status: string
    transcripts: VoiceTranscript[]
    startedAt?: number
    loadId?: string
}
interface AITranscriptionPanelProps {
    activeCall: AITreanscriptionPanelActiveCall
    isLive: boolean
}

export const AITranscriptionPanel: React.FC<AITranscriptionPanelProps> = ({ activeCall, isLive }) => {
    const transcriptEndRef = useRef<HTMLDivElement>(null)
    const transcriptContainerRef = useRef<HTMLDivElement>(null)
    const [reportedProblem, setReportedProblem] = useState(false)
    const [reportingProblem, setReportingProblem] = useState(false)
    const [showBugReportInput, setShowBugReportInput] = useState(false)
    const [bugReportMessage, setBugReportMessage] = useState('')

    const reportProblem = async (customMessage?: string) => {
        setReportingProblem(true)
        try {
            await axios.post('/voip/ringcentral/report-problem', {
                callId: activeCall.callId,
                loadId: activeCall.loadId,
                message: customMessage,
            })
            setReportedProblem(true)
            setShowBugReportInput(false)
            setBugReportMessage('')
        } catch (error) {
            console.error('AITranscriptionPanel::reportProblem:: failed to report a problem', error)
        }
        setReportingProblem(false)
    }

    const handleBugReportSubmit = () => {
        if (bugReportMessage.trim()) {
            reportProblem(bugReportMessage)
        }
    }

    const handleBugReportCancel = () => {
        setShowBugReportInput(false)
        setBugReportMessage('')
    }

    // Auto-scroll to bottom when new transcript arrives - only within the transcript container
    useEffect(() => {
        if (transcriptContainerRef.current && transcriptEndRef.current) {
            // Use scrollTop instead of scrollIntoView to keep scroll within the container
            transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight
        }
    }, [activeCall.transcripts])

    // Helper function to get role-specific styling
    const getRoleStyles = (role: VoiceTranscriptRole) => {
        switch (role) {
            case VoiceTranscriptRole.ASSISTANT:
                return {
                    justifyContent: 'flex-end',
                    bgcolor: 'primary.softBg',
                    borderColor: 'primary.outlinedBorder',
                    textColor: 'primary.plainColor',
                    label: '🤖 Jack (AI):',
                    fontWeight: 'md',
                }
            case VoiceTranscriptRole.TOOL:
                return {
                    justifyContent: 'flex-end',
                    bgcolor: 'warning.softBg',
                    borderColor: 'warning.outlinedBorder',
                    textColor: 'warning.plainColor',
                    label: '🔧 AI Tool:',
                    fontWeight: 'md',
                }
            case VoiceTranscriptRole.USER:
                return {
                    justifyContent: 'flex-start',
                    bgcolor: 'neutral.softBg',
                    borderColor: 'neutral.outlinedBorder',
                    textColor: 'neutral.plainColor',
                    label: '👤 Broker:',
                    fontWeight: 'normal',
                }
            case VoiceTranscriptRole.DISPATCHER:
                return {
                    justifyContent: 'flex-end',
                    bgcolor: 'success.softBg',
                    borderColor: 'success.outlinedBorder',
                    textColor: 'success.plainColor',
                    label: '🎧 Dispatcher:',
                    fontWeight: 'md',
                }
            default:
                console.warn('Unknown voice transcript role:', role)
                return {}
        }
    }

    return (
        <Card
            variant="outlined"
            sx={{
                height: '400px',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.surface',
                '--Card-radius': '12px',
                p: 0,
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    px: 2,
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.level1',
                    borderTopLeftRadius: 'var(--Card-radius)',
                    borderTopRightRadius: 'var(--Card-radius)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography level="title-sm">Live Transcription</Typography>
                        {isLive && (
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: 'success.500',
                                    animation: 'pulse 2s infinite',
                                    '@keyframes pulse': {
                                        '0%': { opacity: 1 },
                                        '50%': { opacity: 0.5 },
                                        '100%': { opacity: 1 },
                                    },
                                }}
                            />
                        )}
                    </Box>
                    {!reportedProblem ? (
                        <Box sx={{ position: 'relative' }}>
                            <IconButton
                                size="sm"
                                variant="plain"
                                color={reportingProblem ? 'primary' : reportedProblem ? 'danger' : 'primary'}
                                onClick={(e) => {
                                    e.preventDefault()
                                    if (showBugReportInput) {
                                        handleBugReportCancel()
                                    } else {
                                        setShowBugReportInput(true)
                                    }
                                }}
                                disabled={reportingProblem}
                                title={showBugReportInput ? 'Cancel bug report' : 'Report AI call problem'}
                            >
                                {reportingProblem ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 20, width: 20 }}>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                            <circle cx="12" cy="12" r="10" stroke="#1976d2" strokeWidth="2" opacity="0.2" />
                                            <circle cx="12" cy="12" r="8" stroke="#1976d2" strokeWidth="2" strokeDasharray="25" strokeDashoffset="10" />
                                        </svg>
                                    </Box>
                                ) : reportedProblem ? (
                                    <CheckCircle fontSize="small" color="error" />
                                ) : (
                                    <HelpOutline fontSize="small" />
                                )}
                            </IconButton>
                            {showBugReportInput && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        right: 0,
                                        mt: 0.5,
                                        p: 1,
                                        bgcolor: 'background.popup',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 'sm',
                                        boxShadow: 'md',
                                        zIndex: 1000,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                        <Textarea
                                            placeholder="Describe the issue..."
                                            value={bugReportMessage}
                                            onChange={(e) => setBugReportMessage(e.target.value)}
                                            sx={{ flex: 1 }}
                                            size="sm"
                                            minRows={1}
                                            maxRows={3}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                                    e.preventDefault()
                                                    handleBugReportSubmit()
                                                }
                                                if (e.key === 'Escape') {
                                                    handleBugReportCancel()
                                                }
                                            }}
                                            autoFocus
                                        />
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <IconButton
                                                size="sm"
                                                variant="solid"
                                                color="primary"
                                                onClick={handleBugReportSubmit}
                                                disabled={!bugReportMessage.trim() || reportingProblem}
                                                title="Send report"
                                            >
                                                {reportingProblem ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 20, width: 20 }}>
                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                            <circle cx="12" cy="12" r="10" stroke="#1976d2" strokeWidth="2" opacity="0.2" />
                                                            <circle
                                                                cx="12"
                                                                cy="12"
                                                                r="8"
                                                                stroke="#1976d2"
                                                                strokeWidth="2"
                                                                strokeDasharray="25"
                                                                strokeDashoffset="10"
                                                            />
                                                        </svg>
                                                    </Box>
                                                ) : (
                                                    <Check fontSize="small" />
                                                )}
                                            </IconButton>
                                            <IconButton
                                                size="sm"
                                                variant="outlined"
                                                color="neutral"
                                                onClick={handleBugReportCancel}
                                                disabled={reportingProblem}
                                                title="Cancel"
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Tooltip title="Report sent. We'll take it from here.">
                            <IconButton variant="soft" color="success" size="sm">
                                <CheckCircle fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Transcript Content */}
            <Box
                ref={transcriptContainerRef}
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    p: 2,
                    bgcolor: 'background.surface',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        bgcolor: 'background.level1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'neutral.500',
                        borderRadius: '3px',
                        '&:hover': {
                            backgroundColor: 'neutral.600',
                        },
                    },
                }}
            >
                {activeCall.transcripts && activeCall.transcripts.length > 0 ? (
                    <Stack spacing={1.5}>
                        {activeCall.transcripts.map((transcript, index) => {
                            const roleStyles = getRoleStyles(transcript.role)
                            return (
                                <Box
                                    key={`${transcript.role}-${index}`}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: roleStyles.justifyContent,
                                        mb: 1.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            maxWidth: transcript.role === VoiceTranscriptRole.TOOL ? '95%' : '85%',
                                            p: 1.5,
                                            borderRadius: transcript.role === VoiceTranscriptRole.TOOL ? 'sm' : 'lg',
                                            bgcolor: roleStyles.bgcolor,
                                            border: '1px solid',
                                            borderColor: roleStyles.borderColor,
                                            boxShadow: 'sm',
                                            ...(transcript.role === VoiceTranscriptRole.TOOL && {
                                                borderStyle: 'dashed',
                                                opacity: 0.9,
                                            }),
                                        }}
                                    >
                                        <Typography
                                            level="body-sm"
                                            sx={{
                                                fontWeight: roleStyles.fontWeight,
                                                color: 'text.primary',
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                wordBreak: 'break-word',
                                                ...(transcript.role === VoiceTranscriptRole.TOOL && {
                                                    fontStyle: 'italic',
                                                    fontSize: '0.825rem',
                                                }),
                                                '& strong': {
                                                    color: roleStyles.textColor,
                                                    mr: 0.5,
                                                },
                                            }}
                                        >
                                            <strong>{roleStyles.label}</strong>
                                            {transcript.tool ? convertVoiceAgentToolCallToMessage(transcript.tool) : transcript.content}
                                        </Typography>
                                    </Box>
                                </Box>
                            )
                        })}
                        <div ref={transcriptEndRef} />
                    </Stack>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        <Typography level="body-sm" color="neutral">
                            {isLive ? 'Waiting for conversation to start...' : 'No transcription available'}
                        </Typography>
                        {isLive && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {[0, 1, 2].map((i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            width: 4,
                                            height: 4,
                                            borderRadius: '50%',
                                            bgcolor: 'primary.300',
                                            animation: `bounce 1.4s infinite ease-in-out both`,
                                            animationDelay: `${i * 0.16}s`,
                                            '@keyframes bounce': {
                                                '0%, 80%, 100%': {
                                                    transform: 'scale(0)',
                                                },
                                                '40%': {
                                                    transform: 'scale(1)',
                                                },
                                            },
                                        }}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Card>
    )
}
